<template>
    <div>
        <h2
            class="text-lg font-medium leading-7 tracking-[-0.02em] text-iron-700"
        >
            {{ $t("configurator.calendars") }}
        </h2>

        <p class="text-sm tracking-[-0.01em] text-iron-500">
            {{ $t("configurator.calendars_description") }}
        </p>

        <div class="mt-8 grid gap-4">
            <div v-if="integrations.length > 0">
                <fieldset class="mt-2">
                    <legend class="sr-only">Event Type Integrations</legend>
                    <div class="-space-y-px rounded-md bg-white">
                        <!-- Checked: "bg-z-primary-50 border-z-primary-200 z-10", Not Checked: "border-iron-200" -->

                        <div
                            v-for="(integration, i) in integrations"
                            :key="i"
                            @click="selectIntegration(integration)"
                            :class="{
                                'opacity-50 cursor-not-allowed': !integration.isValid,
                            }"
                        >
                            <label
                                :data-cy="'integration-' + i"
                                class="relative flex w-full cursor-pointer items-center border p-4"
                                :class="{
                                    'rounded-md': integrations.length === 1,
                                    'rounded-bl-md rounded-br-md':
                                        integrations.length === i + 1,
                                    'rounded-tl-md rounded-tr-md': i === 0,
                                    'border-z-primary-200 bg-z-primary-50':
                                        selectedIntegration?.id ===
                                        integration.id,
                                }"
                            >
                                <input
                                    :disabled="!integration.isValid"
                                    type="radio"
                                    :checked="
                                        selectedIntegration?.id ===
                                        integration.id
                                    "
                                    name="integration"
                                    class="mt-0.5 h-4 w-4 cursor-pointer border-iron-300 text-z-primary-600 focus:ring-z-primary-500"
                                    aria-labelledby="privacy-setting-0-label"
                                    aria-describedby="privacy-setting-0-description"
                                />
                                <div class="ml-3 flex w-full flex-col">
                                    <!-- Checked: "text-z-primary-900", Not Checked: "text-iron-900" -->
                                    <Integration
                                        :integration="integration"
                                        :deletable="false"
                                    />
                                </div>
                            </label>
                        </div>
                    </div>
                </fieldset>
            </div>

            <div v-if="integrationCalendars.length > 0">
                <label
                    for="email"
                    class="mt-4 block text-sm font-medium text-iron-700"
                >
                    {{ $t("event_types.create_event_type.choose_calendar") }}
                </label>
                <fieldset class="mt-1">
                    <legend class="sr-only">EventTypes for integration</legend>
                    <div class="-space-y-px rounded-md bg-white">
                        <!-- Checked: "bg-z-primary-50 border-z-primary-200 z-10", Not Checked: "border-iron-200" -->

                        <RadioGroup
                            v-model="selectedCalendar"
                            :disabled="loading"
                        >
                            <div
                                class="relative -space-y-px rounded-md bg-white"
                            >
                                <RadioGroupOption
                                    v-for="(
                                        integration, i
                                    ) in integrationCalendars"
                                    :key="integration.id"
                                    v-slot="{ checked, active }"
                                    as="template"
                                    :value="integration.id"
                                >
                                    <div
                                        :class="[
                                            i === 0
                                                ? 'rounded-tl-md rounded-tr-md'
                                                : '',
                                            i + 1 ===
                                            integrationCalendars.length
                                                ? 'rounded-bl-md rounded-br-md'
                                                : '',
                                            checked
                                                ? 'z-10 border-z-primary-200 bg-z-primary-50'
                                                : 'border-iron-200',
                                            'relative flex cursor-pointer flex-col border p-4 focus:outline-none md:grid md:pl-4 md:pr-6',
                                        ]"
                                        @click="assignIntegration(integration)"
                                    >
                                        <div class="flex items-center text-sm">
                                            <div
                                                :class="[
                                                    checked
                                                        ? 'border-transparent bg-z-primary-600'
                                                        : 'border-iron-300 bg-white',
                                                    active
                                                        ? 'ring-2 ring-z-primary-500 ring-offset-2'
                                                        : '',
                                                    'flex h-4 w-4 items-center justify-center rounded-full border',
                                                ]"
                                                aria-hidden="true"
                                            >
                                                <div
                                                    class="h-1.5 w-1.5 rounded-full bg-white"
                                                />
                                            </div>
                                            <RadioGroupLabel
                                                as="span"
                                                :class="[
                                                    checked
                                                        ? 'text-z-primary-900'
                                                        : 'text-iron-900',
                                                    'ml-3 truncate font-medium',
                                                ]"
                                                >{{
                                                    integration.name
                                                }}</RadioGroupLabel
                                            >
                                        </div>
                                    </div>
                                </RadioGroupOption>
                            </div>
                        </RadioGroup>
                    </div>
                </fieldset>
            </div>

            <div
                v-if="
                    integrations.length <= 0 &&
                    integrationCalendars.length <= 0 &&
                    loaded
                "
            >
                <Link href="/integrations">
                    <PromoBanner :is-paid="false">
                        <template #title>{{
                            $t("event_types.calendars.banner.title")
                        }}</template>
                        <template #description>{{
                            $t("event_types.calendars.banner.description")
                        }}</template>
                        <template #icon>
                            <CalendarOff :size="20" class="text-[#E92F34]" />
                        </template>
                    </PromoBanner>
                </Link>
            </div>
        </div>
        <!-- <DialogFooter>
                <ZButton @click="assignIntegration" class="ml-4" type="button">
                    {{ $t("event_types.event_detail.assign_integration") }}
                </ZButton>
            </DialogFooter> -->
    </div>
</template>

<script lang="ts" setup>
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import axios from "axios";
import { onMounted, ref } from "vue";
import route from "ziggy-js";
import Integration from "../../Integrations/components/GoogleIntegration.vue";
import { router } from "@inertiajs/vue3";
import PromoBanner from "../../Atomic/PromoBanner.vue";
import { CalendarOff } from "lucide-vue-next";
import { Link } from "@inertiajs/vue3";
type IntegrationCalendar = {
    id: number;
    name: string;
    default: boolean;
};

const selectedIntegration = ref();
const integrationCalendars = ref<IntegrationCalendar[]>([]);
const selectedCalendar = ref<number>();
const calendarIntegrationId = ref<number>();
const integrations = ref([]);
const open = ref(false);
const loading = ref(false);
const loaded = ref(false);

const props = defineProps({
    eventType: {
        type: Object,
        required: true,
    },
    eventTypeUser: {
        type: Object,
        required: true,
    },
});

onMounted(async () => {
    await getIntegrations();
    if (props.eventTypeUser.integration_id) {
        selectIntegrationById(
            props.eventTypeUser.integration_id,
            props.eventTypeUser.integration_data.id,
        );
    }

    loaded.value = true;
});

async function getIntegrations() {
    try {
        const res = await axios.get(route("app.integrations.index-json"));
        integrations.value = res.data.data;
    } catch (err) {
        console.error(err);
        return [];
    }
}

async function getCalendarsForIntegration(integrationId: number) {
    try {
        const res = await axios.get(
            route("app.integrations.calendars.index", {
                integration: integrationId,
            }),
        );

        integrationCalendars.value = res.data.data;

        // selectedCalendar.value = integrationCalendars.value[0].id;
    } catch (err) {
        console.error(err);
    }
}

async function selectIntegration(integration: any) {
    if (!integration.isValid) {
        return;
    }

    if (
        selectedIntegration.value &&
        selectedIntegration.value.id === integration.id
    ) {
        selectedIntegration.value = undefined;
        calendarIntegrationId.value = undefined;
        integrationCalendars.value = [];
        selectedCalendar.value = undefined;
        return;
    }

    await getCalendarsForIntegration(integration.id);
    selectedIntegration.value = integration;
    calendarIntegrationId.value = integration.id;
}

async function selectIntegrationById(
    integrationId: number,
    integrationCalendarId: number,
) {
    const integration = integrations.value.find((i) => i.id === integrationId);

    if (integration) {
        await selectIntegration(integration);
        selectedCalendar.value = integrationCalendarId;
    }
}

async function assignIntegration(calendarIntegration: IntegrationCalendar) {
    loading.value = true;

    try {
        const data: any = {
            integration_id: undefined,
            integration_calendar_id: undefined,
            integration_calendar_name: undefined,
            integration_calendar_default: undefined,
        };

        if (
            selectedIntegration.value !== undefined &&
            selectedCalendar.value !== calendarIntegration.id
        ) {
            selectedCalendar.value = calendarIntegration.id;
            data.integration_id = selectedIntegration.value.id;
            data.integration_calendar_id = calendarIntegration.id;
            data.integration_calendar_name = calendarIntegration.name;
            data.integration_calendar_default = calendarIntegration.default;
        } else {
            selectedCalendar.value = undefined;
            selectedIntegration.value = undefined;
            calendarIntegrationId.value = undefined;
            integrationCalendars.value = [];
        }

        router.post(
            route("app.event-types.integration.assign", {
                eventType: props.eventType.id,
            }),
            data,
            {
                onFinish: () => {
                    open.value = false;
                },
            },
        );
    } catch (err) {
        console.log(err);
        return;
    } finally {
        loading.value = false;
    }
}
</script>
